import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

const NotificationContext = createContext(null);

const getNotificationType = (message) => {
  if (/成功|已发送|已分享|已提交|已复制|完成/.test(message)) return 'success';
  if (/失败|错误|无法|异常/.test(message)) return 'error';
  if (/请先|请输入|请填写|至少|刷新页面|登录/.test(message)) return 'warning';
  return 'info';
};

const typeStyles = {
  success: {
    border: 'border-emerald-200',
    iconBg: 'bg-emerald-100',
    iconText: 'text-emerald-700',
    title: '操作成功',
  },
  error: {
    border: 'border-red-200',
    iconBg: 'bg-red-100',
    iconText: 'text-red-700',
    title: '操作失败',
  },
  warning: {
    border: 'border-amber-200',
    iconBg: 'bg-amber-100',
    iconText: 'text-amber-700',
    title: '需要处理',
  },
  info: {
    border: 'border-sage-200',
    iconBg: 'bg-sage-100',
    iconText: 'text-sage-700',
    title: '提示',
  },
};

const NotificationIcon = ({ type }) => {
  const commonProps = {
    className: 'h-5 w-5',
    fill: 'none',
    stroke: 'currentColor',
    viewBox: '0 0 24 24',
  };

  if (type === 'success') {
    return (
      <svg {...commonProps}>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
    );
  }

  if (type === 'error') {
    return (
      <svg {...commonProps}>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    );
  }

  if (type === 'warning') {
    return (
      <svg {...commonProps}>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      </svg>
    );
  }

  return (
    <svg {...commonProps}>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 21a9 9 0 100-18 9 9 0 000 18z" />
    </svg>
  );
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const originalAlertRef = useRef(null);

  const dismissNotification = useCallback((id) => {
    setNotifications((items) => items.filter((item) => item.id !== id));
  }, []);

  const notify = useCallback((message, options = {}) => {
    const text = String(message || '').trim();
    if (!text) return;

    const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const type = options.type || getNotificationType(text);
    const duration = options.duration ?? (type === 'error' ? 6000 : 4200);

    setNotifications((items) => [
      { id, message: text, type },
      ...items.slice(0, 3),
    ]);

    window.setTimeout(() => dismissNotification(id), duration);
  }, [dismissNotification]);

  useEffect(() => {
    originalAlertRef.current = window.alert;
    window.alert = (message) => notify(message);

    return () => {
      window.alert = originalAlertRef.current;
    };
  }, [notify]);

  return (
    <NotificationContext.Provider value={{ notify }}>
      {children}
      <div className="fixed right-4 top-4 z-[100] flex w-[calc(100vw-2rem)] max-w-sm flex-col gap-3 pointer-events-none">
        {notifications.map((notification) => {
          const styles = typeStyles[notification.type] || typeStyles.info;
          return (
            <div
              key={notification.id}
              className={`pointer-events-auto flex items-start gap-3 rounded-lg border ${styles.border} bg-white/95 px-4 py-3 shadow-zen backdrop-blur-md`}
              role="status"
            >
              <div className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${styles.iconBg} ${styles.iconText}`}>
                <NotificationIcon type={notification.type} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-sage-900">{styles.title}</div>
                <div className="mt-1 text-sm leading-relaxed text-sage-700">{notification.message}</div>
              </div>
              <button
                type="button"
                onClick={() => dismissNotification(notification.id)}
                className="rounded-md p-1 text-sage-400 transition-colors hover:bg-sage-100 hover:text-sage-700"
                aria-label="关闭提示"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          );
        })}
      </div>
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};
